import type { AdsManagerSnapshotFailureCode } from "@shared/ads-manager";

export type AdsManagerSnapshotState = "idle" | "queued" | "refreshing" | "error";

export type AdsManagerSnapshotMetadata = {
  updatedAt?: string | null;
  fetchedAt: string | null;
  lastSuccessfulCollectionAt?: string | null;
  version: number;
  state: AdsManagerSnapshotState;
  range: { since: string; until: string };
  accountId: string;
  failureCode?: AdsManagerSnapshotFailureCode;
  retryNotBefore?: string;
  recovery?: import("@shared/ads-manager").AdsManagerRecovery;
  scheduledRecoveryPossible?: boolean;
  dailyCoverage?: import("@shared/ads-manager").AdsManagerDailyCoverage;
  maintenanceCoverage?: import("@shared/ads-manager").AdsManagerDailyCoverage;
  /** Legacy rolling-maintenance summary, NOT selected-range coverage. */
  coverageState?: "complete" | "partial" | "stale" | "waiting" | "failed";
};

export function adsManagerDailyCoverageLabel(coverage: import("@shared/ads-manager").AdsManagerDailyCoverage): string {
  if (coverage.dailyReuse === "exact-range-fallback")
    return `${coverage.savedDays}/${coverage.totalDays} days saved. This report uses provider exact-range metrics because saved daily coverage differs. Saved days are retained.`;
  return `${coverage.savedDays}/${coverage.totalDays} days saved · ${coverage.freshDays} fresh. ${
    coverage.exactRange === "available" ? "Exact-range report available."
      : "Saved additive reporting is available when coverage and entity metadata are complete. Unique reach remains unavailable without matching exact-range evidence."
  }`;
}

export type AdsManagerSnapshotScope = {
  brandId: string;
  accountId: string;
  range: { since: string; until: string };
};

export const ADS_MANAGER_SNAPSHOT_STATUS_POLL_MS = 2_000;
export const ADS_MANAGER_SNAPSHOT_STATUS_MAX_POLLS = 180;

/** Describe the failed attempt only. Recovery actions are decided separately. */
export function adsManagerSnapshotFailureLabel(code?: AdsManagerSnapshotFailureCode): string {
  if (code === "snapshot_execution_budget")
    return "The last pass reached its collection deadline. Saved progress is retained.";
  if (code === "snapshot_recovery_exhausted")
    return "Recovery stopped after its bounded attempt allowance without a complete replacement. Saved progress is retained.";
  if (code === "snapshot_read_authentication")
    return "The provider rejected account access. Restore access before another attempt.";
  if (code === "snapshot_read_field_contract")
    return "The provider rejected the reporting fields. The integration needs correction before another attempt.";
  if (code === "snapshot_read_rate_limit")
    return "The provider limited reporting requests during the last attempt.";
  if (code === "provider_freshness_regressed")
    return "The update was rejected because matching performance rows have older provider timestamps than retained performance evidence. This does not establish why the source timestamps regressed.";
  if (code === "performance_requalification_required")
    return "Daily source timestamps do not yet qualify to replace the saved range report. Replacement requires qualified source evidence.";
  if (code === "performance_grain_incompatible" || code === "performance_scope_incompatible")
    return "The replacement uses an incompatible reporting basis. Replacement still requires verified source evidence.";
  if (code === "performance_provenance_missing")
    return "Verified source timestamps are unavailable. Replacement requires verified source evidence.";
  if (code === "performance_identity_missing")
    return "Reporting evidence is incomplete: previously saved rows are still missing after bounded recovery. Retry will only help if the source returns the missing evidence.";
  if (code === "performance_timestamp_missing")
    return "Previously saved identities are present, but their source timestamps are missing or invalid. Replacement requires verified source timestamps.";
  if (code === "snapshot_read_validation" || code === "malformed_hierarchy" || code === "incomplete_provider_data" || code === "empty_replacement")
    return "The report could not be validated. This pass stopped. The reporting issue must be resolved before a replacement can qualify.";
  if (code && ["snapshot_read_timeout", "snapshot_read_network", "snapshot_read_retryable_http"].includes(code))
    return code === "snapshot_read_timeout" ? "A provider read timed out during the last collection attempt."
      : code === "snapshot_read_network" ? "A provider read failed with a network error during the last collection attempt."
      : "A provider read returned a retryable HTTP failure during the last collection attempt.";
  return "The last collection attempt failed.";
}

function eligibilityLabel(value?: string): string {
  return value && Number.isFinite(Date.parse(value))
    ? ` No earlier than ${new Date(value).toLocaleString()}; this is eligibility, not a promised retry time.`
    : "";
}

/** Use retained stop evidence, never infer a phase, day or retry count from a code. */
function attemptLabel(stop?: import("@shared/ads-manager").AdsManagerRecovery["stop"]): string {
  if (!stop) return "";
  const phases: Record<string, string> = {
    reporting: "range reporting", daily_reporting: "daily reporting",
    daily_metadata_recovery: "daily metadata recovery", daily_population_recovery: "daily population recovery",
    entity_metadata: "entity metadata", campaign_metadata: "campaign metadata",
    creative_metadata: "creative metadata", campaign_delivery: "campaign delivery", assembly: "report assembly",
  };
  const details = [
    phases[stop.requestPhase ?? ""],
    stop.reportingDay && /^\d{4}-\d{2}-\d{2}$/.test(stop.reportingDay) ? stop.reportingDay : null,
    Number.isSafeInteger(stop.attempts) && stop.attempts! > 0 ? `${stop.attempts} read attempt(s) in the stopped read` : null,
  ].filter(Boolean);
  return `${stop.stopReason === "exhausted" ? " The read retry allowance for this collection attempt was exhausted." : ""}${
    details.length ? ` Last stop: ${details.join(" · ")}.` : ""
  }`;
}

export function adsManagerRecoveryGuidance(snapshot: AdsManagerSnapshotMetadata): string {
  if (snapshot.state === "refreshing") return "Collection is active. Sync is not needed.";
  const failure = snapshot.state === "error"
    ? `${adsManagerSnapshotFailureLabel(snapshot.failureCode)}${attemptLabel(snapshot.recovery?.stop)} ` : "";
  if (snapshot.retryNotBefore) return `${failure}Provider cooldown is active. Sync cannot bypass this wait.`;
  if (snapshot.recovery?.escalated)
    return `${failure}Automatic recovery reached its cumulative limit. Saved progress is retained. An operator must investigate before restarting.`;
  if (snapshot.state === "queued") return `Continuation is queued in the existing refresh lane.${
    eligibilityLabel(snapshot.recovery?.nextEligibleAt)
  } Sync is not needed; saved progress will be reused.`;
  if (snapshot.state === "error") return `${failure}${
    snapshot.scheduledRecoveryPossible
      ? `This current rolling range remains eligible for later scheduled recovery. Sync is not required.${
        eligibilityLabel(snapshot.recovery?.nextEligibleAt ?? snapshot.recovery?.restartNotBefore)
      } Saved progress is retained.`
      : "This stopped range will not restart automatically. Use Sync to retry after any access or reporting issue is resolved; repeated Sync does not bypass source checks."
  }`;
  return "";
}

export function adsManagerSelectedCoverageLabel(snapshot: AdsManagerSnapshotMetadata): string | null {
  const coverage = snapshot.dailyCoverage;
  if (!coverage) return null;
  return `Selected dates: ${coverage.savedDays}/${coverage.totalDays} days saved.${
    coverage.savedDays < coverage.totalDays ? " Selected daily coverage is incomplete." : ""
  }${coverage.completedDays != null ? ` ${coverage.completedDays} completed days.` : ""}${
    coverage.partialToday ? " Today is partial." : ""
  } ${coverage.freshDays}/${coverage.totalDays} days fresh by collection age.${
    coverage.exactRange === "available" ? " Saved exact-range report available." : ""
  }`;
}

export function adsManagerMaintenanceCoverageLabel(snapshot: AdsManagerSnapshotMetadata): string | null {
  const coverage = snapshot.maintenanceCoverage;
  if (!coverage) return null;
  return `Rolling-history maintenance: ${coverage.savedDays}/${coverage.totalDays} days saved · ${coverage.freshDays}/${coverage.totalDays} fresh.${
    coverage.savedDays < coverage.totalDays ? " History coverage is incomplete."
      : coverage.freshDays < coverage.totalDays ? " History updates are due." : ""
  } This is separate from the selected report and provider freshness.`;
}
/** Version counts successful payloads, not attempts. Order same-version states
 * by their durable transition time; fall back to query receipt for old servers. */
export function effectiveAdsManagerSnapshot(
  report: AdsManagerSnapshotMetadata | null,
  status: AdsManagerSnapshotMetadata | null,
  reportReceivedAt: number,
  statusReceivedAt: number,
): AdsManagerSnapshotMetadata | null {
  if (!report) return status;
  if (!status) return report;
  if (status.version !== report.version) return status.version > report.version ? status : report;
  const reportTime = Date.parse(report.updatedAt ?? "");
  const statusTime = Date.parse(status.updatedAt ?? "");
  if (Number.isFinite(reportTime) && Number.isFinite(statusTime) && reportTime !== statusTime)
    return statusTime > reportTime ? status : report;
  return statusReceivedAt > reportReceivedAt ? status : report;
}

export function isAdsManagerSnapshotMetadata(value: unknown): value is AdsManagerSnapshotMetadata {
  if (!value || typeof value !== "object") return false;
  const snapshot = value as Partial<AdsManagerSnapshotMetadata>;
  return (snapshot.fetchedAt === null || typeof snapshot.fetchedAt === "string")
    && Number.isSafeInteger(snapshot.version)
    && (snapshot.state === "idle" || snapshot.state === "queued" || snapshot.state === "refreshing" || snapshot.state === "error")
    && typeof snapshot.accountId === "string"
    && typeof snapshot.range?.since === "string"
    && typeof snapshot.range?.until === "string";
}

export function snapshotMatchesScope(
  snapshot: unknown,
  scope: Pick<AdsManagerSnapshotScope, "accountId" | "range">,
): snapshot is AdsManagerSnapshotMetadata {
  return isAdsManagerSnapshotMetadata(snapshot)
    && snapshot.accountId === scope.accountId
    && snapshot.range.since === scope.range.since
    && snapshot.range.until === scope.range.until;
}

export function adsManagerSnapshotUpdatedLabel(fetchedAt: string | null | undefined, now = Date.now()): string | null {
  if (!fetchedAt) return null;
  const timestamp = Date.parse(fetchedAt);
  if (!Number.isFinite(timestamp) || timestamp > now) return null;
  const minutes = Math.floor((now - timestamp) / 60_000);
  return `Updated ${minutes} min ago`;
}

export function adsManagerSnapshotCollectionLabel(snapshot: AdsManagerSnapshotMetadata, now = Date.now()): string | null {
  const collection = adsManagerSnapshotUpdatedLabel(snapshot.lastSuccessfulCollectionAt, now);
  return collection ? collection.replace("Updated", "Last collection") : adsManagerSnapshotUpdatedLabel(snapshot.fetchedAt, now);
}

/** Match only the already-cached grid and exact-entity queries for this report. */
export function matchesAdsManagerSnapshotReportQueryKey(
  queryKey: readonly unknown[],
  scope: AdsManagerSnapshotScope,
): boolean {
  if (queryKey[0] !== "/api/brands" || queryKey[1] !== scope.brandId || queryKey[2] !== "ads-manager") return false;
  if (queryKey[3] === "exact") {
    return queryKey[4] === scope.accountId
      && queryKey[5] === scope.range.since
      && queryKey[6] === scope.range.until;
  }
  if (typeof queryKey[3] !== "string") return false;
  const params = new URLSearchParams(queryKey[3]);
  return params.get("accountId") === scope.accountId
    && params.get("since") === scope.range.since
    && params.get("until") === scope.range.until;
}